import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import ReactECharts from "echarts-for-react";
import { Card, DatePicker, Button, Space, Typography, Spin, Empty, Alert, Collapse, Tag, Tooltip, App } from "antd";
import { RefreshCw, TrendingUp, Wallet, ArrowUpRight, ArrowDownRight, User, LayoutDashboard, Info, Building2, Briefcase, Tags } from "lucide-react";
import dayjs from "dayjs";
import apiService from "../../services/api";
import useTheme from "../../hooks/useTheme";
import { formatExchangeName, formatChartTooltipValue } from "../../utils/formatters";
import HedgePanoramaSection from "../HedgeCharts/HedgePanoramaSection";
import { hasPermission, PERMISSIONS } from "../../utils/permissions";
import { Shield } from "lucide-react";
import { InfoCircleOutlined } from "@ant-design/icons";
import { useAdminPanoramaUsers } from "../../hooks/useAdminPanoramaUsers";
import { withOwnerUserId } from "../../utils/ownerScopeParams";
import PanoramaExportButtons from "../common/PanoramaExportButtons";
import {
    resolvePanoramaExportOwnerUserId,
    exportFundTrendsExcel,
} from "../../utils/panoramaExportExcel";
import { capturePanoramaToPdf } from "../../utils/panoramaPdfCapture";
import "./FundTrendsChart.css";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

// 近 N 天、本月均不含「今天」，截止昨天
const QUICK_DATES = [
    { label: "今天", value: () => [dayjs(), dayjs()] },
    {
        label: "近1天",
        value: () => {
            const y = dayjs().subtract(1, "day");
            return [y.startOf("day"), y.endOf("day")];
        },
    },
    {
        label: "近2天",
        value: () => [dayjs().subtract(2, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
    },
    {
        label: "近3天",
        value: () => [dayjs().subtract(3, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
    },
    {
        label: "近7天",
        value: () => [dayjs().subtract(7, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
    },
    {
        label: "近30天",
        value: () => [dayjs().subtract(30, "day").startOf("day"), dayjs().subtract(1, "day").endOf("day")],
    },
    {
        label: "本月",
        value: () => {
            const end = dayjs().subtract(1, "day").endOf("day");
            const start = dayjs().startOf("month").startOf("day");
            if (start.isAfter(end)) {
                return [end.startOf("day"), end];
            }
            return [start, end];
        },
    },
    { label: "上月", value: () => [dayjs().subtract(1, "month").startOf("month"), dayjs().subtract(1, "month").endOf("month")] },
];

/**
 * 单用户或全平台资金走势（管理员折叠内多实例）
 * @param {{ ownerUserId?: number|null, dateRange: import("dayjs").Dayjs[], equityRecalcNonce?: number }} props
 */
function FundTrendsChartBody({ ownerUserId = null, dateRange, equityRecalcNonce = 0 }) {
    const { isDark } = useTheme();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [totalTrends, setTotalTrends] = useState([]);
    const [accountTrends, setAccountTrends] = useState([]);
    const [exchangeTrendRows, setExchangeTrendRows] = useState([]);
    const [businessLineTrends, setBusinessLineTrends] = useState([]);
    const [strategyTypeTrends, setStrategyTypeTrends] = useState([]);

    const [canViewHedge] = useState(() => {
        try {
            const u = JSON.parse(localStorage.getItem("user") || "{}");
            return hasPermission(u, PERMISSIONS.VIEW_HEDGE_GROUPS);
        } catch {
            return false;
        }
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const base = {
                date_from: dateRange[0].format("YYYY-MM-DD"),
                date_to: dateRange[1].format("YYYY-MM-DD"),
            };
            const params = withOwnerUserId(base, ownerUserId);

            const [totalRes, accountsRes, exchangesRes, blRes, stRes] = await Promise.all([
                apiService.equity.getTotalTrends(params),
                apiService.equity.getAccountTrends(params),
                apiService.equity.getExchangeTrends(params),
                apiService.equity.getBusinessLineTrends(params),
                apiService.equity.getStrategyTypeTrends(params),
            ]);

            if (totalRes.data?.success) setTotalTrends(totalRes.data.data || []);
            if (accountsRes.data?.success) setAccountTrends(accountsRes.data.data || []);
            if (exchangesRes.data?.success) setExchangeTrendRows(exchangesRes.data.data || []);
            if (blRes.data?.success) setBusinessLineTrends(blRes.data.data || []);
            if (stRes.data?.success) setStrategyTypeTrends(stRes.data.data || []);
        } catch (err) {
            console.error("Fetch trends error:", err);
            setError("获取数据失败，请重试");
        } finally {
            setLoading(false);
        }
    }, [dateRange, ownerUserId]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    useEffect(() => {
        if (ownerUserId != null) return;
        if (equityRecalcNonce === 0) return;
        fetchData();
    }, [equityRecalcNonce, ownerUserId, fetchData]);

    const getDualAxisOption = useCallback((name, dates, netAssets, pnls, hideLegend = false) => {
        const textColor = isDark ? "rgba(255, 255, 255, 0.45)" : "#64748b";
        const splitLineColor = isDark ? "rgba(255, 255, 255, 0.05)" : "rgba(0,0,0,0.05)";
        const axisLineColor = isDark ? "rgba(255, 255, 255, 0.15)" : "#e2e8f0";

        return {
            backgroundColor: "transparent",
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "cross" },
                backgroundColor: isDark ? "rgba(30, 35, 45, 0.95)" : "rgba(255, 255, 255, 0.95)",
                borderColor: isDark ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
                textStyle: {
                    color: isDark ? "#f0f0f5" : "#1f2937"
                },
                formatter: (params) => {
                    let res = `<div style="margin-bottom:4px;">${params[0].axisValue}</div>`;
                    params.forEach(item => {
                        res += `<div style="display:flex;justify-content:space-between;gap:20px;align-items:center;">
                            <span>${item.marker} ${item.seriesName}</span>
                            <span style="font-weight:bold">${formatChartTooltipValue(item.value)}</span>
                        </div>`;
                    });
                    return res;
                }
            },
            legend: {
                show: !hideLegend,
                bottom: 0,
                data: ["净资产", "当日盈亏"],
                textStyle: { color: textColor }
            },
            grid: {
                left: "3%",
                right: "4%",
                bottom: hideLegend ? "10%" : "15%",
                top: "15%",
                containLabel: true
            },
            xAxis: {
                type: "category",
                data: dates,
                axisLine: { lineStyle: { color: axisLineColor } },
                axisLabel: { color: textColor },
            },
            yAxis: [
                {
                    type: "value",
                    name: "净资产",
                    nameTextStyle: { color: textColor },
                    scale: true,
                    axisLabel: { color: textColor },
                    splitLine: { lineStyle: { type: "dashed", color: splitLineColor } }
                },
                {
                    type: "value",
                    name: "盈亏",
                    nameTextStyle: { color: textColor },
                    scale: true,
                    axisLabel: { color: textColor },
                    splitLine: { show: false }
                }
            ],
            series: [
                {
                    name: "净资产",
                    type: "line",
                    data: netAssets,
                    smooth: true,
                    lineStyle: { width: 3, color: "#1890ff" },
                    symbol: "circle",
                    itemStyle: { color: "#1890ff" },
                    label: {
                        show: true,
                        position: "top",
                        color: textColor,
                        fontSize: 10,
                        distance: 10,
                        padding: [4, 4],
                        backgroundColor: isDark ? "rgba(0,0,0,0.3)" : "rgba(255,255,255,0.6)",
                        borderRadius: 4,
                        formatter: (params) => params.value ? Math.round(params.value).toLocaleString() : ""
                    },
                    areaStyle: {
                        color: {
                            type: "linear",
                            x: 0, y: 0, x2: 0, y2: 1,
                            colorStops: [{ offset: 0, color: "rgba(24,144,255,0.2)" }, { offset: 1, color: "rgba(24,144,255,0)" }]
                        }
                    }
                },
                {
                    name: "当日盈亏",
                    type: "line",
                    yAxisIndex: 1,
                    smooth: true,
                    data: pnls,
                    lineStyle: { width: 2.5, color: "#fa8c16" },
                    symbol: "circle",
                    itemStyle: { color: "#fa8c16" },
                    label: {
                        show: true,
                        position: "bottom",
                        color: textColor,
                        fontSize: 10,
                        distance: 8,
                        formatter: (params) => (params.value !== 0 && params.value != null ? Math.round(params.value).toLocaleString() : "")
                    }
                }
            ]
        };
    }, [isDark]);

    const translateTradeType = useCallback((type) => {
        if (!type) return "";
        const lower = type.toLowerCase();
        if (lower === "spot") return "现货";
        if (lower === "swap" || lower === "futures") return "合约";
        return type;
    }, []);

    const exchangeVenueGroups = useMemo(() => {
        const m = new Map();
        (exchangeTrendRows || []).forEach((row) => {
            const ex = row.exchange || "";
            if (!m.has(ex)) {
                m.set(ex, { exchange: ex, spotTrends: null, swapTrends: null });
            }
            const g = m.get(ex);
            const tt = String(row.trade_type || "").toLowerCase();
            if (tt === "spot") g.spotTrends = row.trends || [];
            else g.swapTrends = row.trends || [];
        });
        return Array.from(m.values()).sort((a, b) =>
            String(a.exchange).localeCompare(String(b.exchange)),
        );
    }, [exchangeTrendRows]);

    const totalChartOption = useMemo(() => {
        if (!totalTrends.length) return {};
        const dates = totalTrends.map(t => dayjs(t.date).format("MM-DD"));
        const netAssets = totalTrends.map(t => t.total_net_assets);
        const pnls = totalTrends.map(t => t.total_pnl);
        return getDualAxisOption("总走势", dates, netAssets, pnls);
    }, [totalTrends, getDualAxisOption]);

    const latestNetAssets = totalTrends.length > 0 ? (totalTrends[totalTrends.length - 1]?.total_net_assets || 0) : 0;
    const totalPeriodPnl = totalTrends.reduce((sum, t) => sum + (t.total_pnl || 0), 0);

    return (
        <div className="fund-trends-body">
            {ownerUserId != null && (
                <div style={{ marginBottom: 8 }}>
                    <Tag color="blue">用户数据 · ID {ownerUserId}</Tag>
                </div>
            )}
            <div style={{ marginBottom: 12, display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
                <Button type="primary" icon={<RefreshCw size={14} />} onClick={fetchData} loading={loading}>
                    刷新本面板
                </Button>
            </div>

            {error && (
                <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} closable onClose={() => setError(null)} />
            )}

            <div className="stats-cards">
                <Card className="stat-card blue-gradient" variant="borderless">
                    <div className="stat-row-top">
                        <Wallet size={16} color="white" />
                        <span className="stat-label">当前总净资产</span>
                    </div>
                    <div className="stat-value">{latestNetAssets.toLocaleString(undefined, { minimumFractionDigits: 2 })} <small>USDT</small></div>
                </Card>
                <Card className={`stat-card ${totalPeriodPnl >= 0 ? "green-gradient" : "red-gradient"}`} variant="borderless">
                    <div className="stat-row-top">
                        <TrendingUp size={16} color="white" />
                        <span className="stat-label">区间累计总盈亏</span>
                    </div>
                    <div className="stat-value">
                        {totalPeriodPnl >= 0 ? <ArrowUpRight size={16} color="white" /> : <ArrowDownRight size={16} color="white" />}
                        {totalPeriodPnl > 0 ? "+" : ""}{totalPeriodPnl.toLocaleString(undefined, { minimumFractionDigits: 2 })} <small>USDT</small>
                    </div>
                </Card>
            </div>

            <Spin spinning={loading}>
                <Collapse
                    ghost
                    defaultActiveKey={
                        canViewHedge
                            ? ["total", "businessLines", "strategyTypes", "hedge", "exchanges", "accounts"]
                            : ["total", "businessLines", "strategyTypes", "exchanges", "accounts"]
                    }
                    className="trends-collapse"
                    items={[
                        {
                            key: "total",
                            label: <Space><LayoutDashboard size={18} /><strong>总资产与盈亏走势</strong></Space>,
                            children: (
                                <Card variant="borderless" className="chart-wrapper-card">
                                    {totalTrends.length > 0 ? (
                                        <ReactECharts option={totalChartOption} style={{ height: "450px" }} />
                                    ) : <Empty description="区间内暂无数据" />}
                                </Card>
                            )
                        },
                        {
                            key: "businessLines",
                            label: (
                                <Space>
                                    <Briefcase size={18} />
                                    <strong>分业务线走势</strong>
                                    <Tag color="geekblue">{businessLineTrends.length}</Tag>
                                </Space>
                            ),
                            children: (
                                <div className="dimension-grid">
                                    {businessLineTrends.map((row) => {
                                        const label = row.code
                                            ? `${row.name}（${row.code}）`
                                            : row.name || (row.id === 0 ? "未分配" : `ID ${row.id}`);
                                        const dates = (row.trends || []).map((t) => dayjs(t.equity_date).format("MM-DD"));
                                        const netAssets = (row.trends || []).map((t) => t.net_assets);
                                        const pnls = (row.trends || []).map((t) => t.pnl);
                                        return (
                                            <Card key={`bl-${row.id}`} title={label} size="small" className="dimension-card">
                                                {(row.trends || []).length > 0 ? (
                                                    <ReactECharts
                                                        option={getDualAxisOption("bl", dates, netAssets, pnls, true)}
                                                        style={{ height: 260 }}
                                                    />
                                                ) : (
                                                    <Empty description="该业务线在此区间无权益数据" />
                                                )}
                                            </Card>
                                        );
                                    })}
                                    {businessLineTrends.length === 0 && (
                                        <Empty description="暂无业务线汇总数据（需在账号配置中维护业务线）" />
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: "strategyTypes",
                            label: (
                                <Space>
                                    <Tags size={18} />
                                    <strong>分策略类型走势</strong>
                                    <Tag color="purple">{strategyTypeTrends.length}</Tag>
                                </Space>
                            ),
                            children: (
                                <div className="dimension-grid">
                                    {strategyTypeTrends.map((row) => {
                                        const label = row.code
                                            ? `${row.name}（${row.code}）`
                                            : row.name || (row.id === 0 ? "未分配" : `ID ${row.id}`);
                                        const dates = (row.trends || []).map((t) => dayjs(t.equity_date).format("MM-DD"));
                                        const netAssets = (row.trends || []).map((t) => t.net_assets);
                                        const pnls = (row.trends || []).map((t) => t.pnl);
                                        return (
                                            <Card key={`st-${row.id}`} title={label} size="small" className="dimension-card">
                                                {(row.trends || []).length > 0 ? (
                                                    <ReactECharts
                                                        option={getDualAxisOption("st", dates, netAssets, pnls, true)}
                                                        style={{ height: 260 }}
                                                    />
                                                ) : (
                                                    <Empty description="该策略类型在此区间无权益数据" />
                                                )}
                                            </Card>
                                        );
                                    })}
                                    {strategyTypeTrends.length === 0 && (
                                        <Empty description="暂无策略类型汇总数据（需在账号配置中维护策略类型）" />
                                    )}
                                </div>
                            ),
                        },
                        ...(canViewHedge
                            ? [
                                  {
                                      key: "hedge",
                                      label: (
                                          <Space align="center" size={8}>
                                              <Shield size={18} style={{ opacity: 0.9 }} />
                                              <strong>对冲组合分析（资金视角）</strong>
                                              <Tooltip title="与上方资金走势对照：各组区间总交易量（trade_volumes）、组合资金/净资产、区间盈亏与对冲效率；资金与盈亏来自权益快照。">
                                                  <InfoCircleOutlined style={{ fontSize: 14, opacity: 0.55, cursor: "help" }} />
                                              </Tooltip>
                                          </Space>
                                      ),
                                      children: (
                                          <Card variant="borderless" className="chart-wrapper-card">
                                              <HedgePanoramaSection
                                                  dateFrom={dateRange[0].format("YYYY-MM-DD")}
                                                  dateTo={dateRange[1].format("YYYY-MM-DD")}
                                                  ownerUserId={ownerUserId}
                                              />
                                          </Card>
                                      ),
                                  },
                              ]
                            : []),
                        {
                            key: "exchanges",
                            label: (
                                <Space>
                                    <Building2 size={18} />
                                    <strong>分交易所走势</strong>
                                    <Tag color="cyan">{exchangeVenueGroups.length}</Tag>
                                </Space>
                            ),
                            children: (
                                <div className="dimension-grid">
                                    {exchangeVenueGroups.map((venue) => {
                                        const hasSpot = venue.spotTrends && venue.spotTrends.length > 0;
                                        const hasSwap = venue.swapTrends && venue.swapTrends.length > 0;
                                        return (
                                            <Card
                                                key={venue.exchange}
                                                title={
                                                    <span className="exchange-card-title">
                                                        {formatExchangeName(venue.exchange)}
                                                    </span>
                                                }
                                                size="small"
                                                className="dimension-card exchange-venue-card"
                                            >
                                                <div className="exchange-split-charts">
                                                    {hasSpot && (
                                                        <div className="trade-type-subchart">
                                                            <Text type="secondary" className="subchart-label">
                                                                现货（全账号汇总）
                                                            </Text>
                                                            <ReactECharts
                                                                option={getDualAxisOption(
                                                                    "spot",
                                                                    venue.spotTrends.map((t) => dayjs(t.equity_date).format("MM-DD")),
                                                                    venue.spotTrends.map((t) => t.net_assets),
                                                                    venue.spotTrends.map((t) => t.pnl),
                                                                    true,
                                                                )}
                                                                style={{ height: 240 }}
                                                            />
                                                        </div>
                                                    )}
                                                    {hasSwap && (
                                                        <div className="trade-type-subchart">
                                                            <Text type="secondary" className="subchart-label">
                                                                合约（全账号汇总）
                                                            </Text>
                                                            <ReactECharts
                                                                option={getDualAxisOption(
                                                                    "swap",
                                                                    venue.swapTrends.map((t) => dayjs(t.equity_date).format("MM-DD")),
                                                                    venue.swapTrends.map((t) => t.net_assets),
                                                                    venue.swapTrends.map((t) => t.pnl),
                                                                    true,
                                                                )}
                                                                style={{ height: 240 }}
                                                            />
                                                        </div>
                                                    )}
                                                    {!hasSpot && !hasSwap && (
                                                        <Empty description="该交易所在此区间无权益数据" />
                                                    )}
                                                </div>
                                            </Card>
                                        );
                                    })}
                                    {exchangeVenueGroups.length === 0 && (
                                        <Empty description="暂无交易所汇总数据" />
                                    )}
                                </div>
                            ),
                        },
                        {
                            key: "accounts",
                            label: <Space><User size={18} /><strong>分账号走势</strong> <Tag color="blue">{accountTrends.length}</Tag></Space>,
                            children: (
                                <div className="dimension-grid">
                                    {accountTrends.map((acc) => {
                                        const dates = acc.trends.map(t => dayjs(t.equity_date).format("MM-DD"));
                                        const netAssets = acc.trends.map(t => t.net_assets);
                                        const pnls = acc.trends.map(t => t.pnl);
                                        const option = getDualAxisOption(acc.account, dates, netAssets, pnls, true);

                                        return (
                                            <Card key={`${acc.account}-${acc.exchange}-${acc.trade_type}`} title={
                                                <div className="card-title-flex">
                                                    <span>{acc.account} <small style={{ fontWeight: 400, opacity: 0.6 }}>({acc.exchange}, {translateTradeType(acc.trade_type)})</small></span>
                                                </div>
                                            } size="small" className="dimension-card">
                                                <ReactECharts option={option} style={{ height: "260px" }} />
                                            </Card>
                                        );
                                    })}
                                    {accountTrends.length === 0 && <Empty description="暂无账号数据" />}
                                </div>
                            )
                        }
                    ]}
                />
            </Spin>
        </div>
    );
}

function FundTrendsChart() {
    const { modal, message: msg } = App.useApp();
    const panoramaCaptureRef = useRef(null);
    const { isAdmin, adminUsers, loadingUsers } = useAdminPanoramaUsers();
    const [dateRange, setDateRange] = useState([
        dayjs().subtract(7, "day").startOf("day"),
        dayjs().subtract(1, "day").endOf("day"),
    ]);
    const [activeQuickDate, setActiveQuickDate] = useState("近7天");
    const [adminPanoramaKey, setAdminPanoramaKey] = useState(["all"]);
    const [recalcLoading, setRecalcLoading] = useState(false);
    const [equityRecalcNonce, setEquityRecalcNonce] = useState(0);

    const exportOwnerUserId = useMemo(
        () => resolvePanoramaExportOwnerUserId(isAdmin, adminPanoramaKey),
        [isAdmin, adminPanoramaKey],
    );

    const dateFromStr = dateRange[0]?.format("YYYY-MM-DD");
    const dateToStr = dateRange[1]?.format("YYYY-MM-DD");

    const handleExportExcel = useCallback(async () => {
        if (!dateFromStr || !dateToStr) return;
        const hide = msg.loading("正在导出 Excel...", 0);
        try {
            await exportFundTrendsExcel({
                dateFrom: dateFromStr,
                dateTo: dateToStr,
                ownerUserId: exportOwnerUserId,
            });
            msg.success("Excel 已下载");
        } catch (e) {
            console.error(e);
            msg.error(e.apiMessage || e.message || "导出失败");
        } finally {
            hide();
        }
    }, [dateFromStr, dateToStr, exportOwnerUserId, msg]);

    const handleExportPdf = useCallback(async () => {
        if (!dateFromStr || !dateToStr) return;
        const hide = msg.loading("正在展开图表并生成 PDF...", 0);
        try {
            await capturePanoramaToPdf(
                panoramaCaptureRef.current,
                `资金与盈亏走势_${dateFromStr}_${dateToStr}`,
            );
            msg.success("PDF 已下载");
        } catch (e) {
            console.error(e);
            msg.error(e.message || "PDF 导出失败");
        } finally {
            hide();
        }
    }, [dateFromStr, dateToStr, msg]);

    const handleQuickDate = (label) => {
        const option = QUICK_DATES.find(d => d.label === label);
        if (option) {
            setDateRange(option.value());
            setActiveQuickDate(label);
        }
    };

    const handleRecalculateAll = () => {
        modal.confirm({
            title: "确认重算历史数据？",
            content: "该操作将遍历所有历史 snapshot 重新计算盈亏与净资产，并在后台执行。耗时较长，请稍后刷新查看结果。",
            onOk: async () => {
                setRecalcLoading(true);
                try {
                    const res = await apiService.equity.recalculateAll();
                    if (res.data?.success) {
                        msg.success(res.data.data?.message || res.data.message || "已成功开启后台重算任务");
                        setEquityRecalcNonce((n) => n + 1);
                    }
                } catch (err) {
                    console.error("Recalculate all error:", err);
                    msg.error("触发重算失败");
                } finally {
                    setRecalcLoading(false);
                }
            }
        });
    };

    const bodyCommon = { dateRange, equityRecalcNonce };

    const pageHeader = (
        <div className="dashboard-header-container">
            <div className="header-left">
                <Title level={4} className="fund-page-title" style={{ margin: 0, color: "inherit" }}>
                    资金与盈亏走势
                    <Tooltip title="多维度资金变动分析：支持现货与合约净资产独立核算，盈亏自动追踪。">
                        <Info size={16} style={{ marginLeft: 8, opacity: 0.45, cursor: "help" }} />
                    </Tooltip>
                    {isAdmin && (
                        <Tag color="processing" style={{ marginLeft: 12, fontWeight: 500 }}>
                            管理员可展开查看各用户名下明细
                        </Tag>
                    )}
                </Title>
                <Text type="secondary" className="fund-page-subtitle">
                    监测账户净值波动与每日盈亏表现
                </Text>
            </div>
            <div className="header-controls">
                <div className="quick-date-buttons" role="group" aria-label="快捷日期">
                    {QUICK_DATES.map((item) => (
                        <button
                            key={item.label}
                            type="button"
                            className={`quick-date-btn ${activeQuickDate === item.label ? "active" : ""}`}
                            onClick={() => handleQuickDate(item.label)}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>
                <RangePicker
                    value={dateRange}
                    onChange={(dates) => {
                        if (dates) setDateRange(dates);
                        setActiveQuickDate("");
                    }}
                    allowClear={false}
                    className="custom-range-picker fund-header-range"
                />
                <PanoramaExportButtons onExportExcel={handleExportExcel} onExportPdf={handleExportPdf} />
                {isAdmin && (
                    <Tooltip title="重新计算所有历史快照的净资产与盈亏（后台执行，影响全平台）">
                        <Button
                            danger
                            icon={<TrendingUp size={14} />}
                            onClick={handleRecalculateAll}
                            loading={recalcLoading}
                            className="fund-recalc-btn"
                        >
                            重算历史数据
                        </Button>
                    </Tooltip>
                )}
            </div>
        </div>
    );

    if (!isAdmin) {
        return (
            <div className="fund-trends-container" ref={panoramaCaptureRef}>
                {pageHeader}
                <FundTrendsChartBody ownerUserId={null} {...bodyCommon} />
            </div>
        );
    }

    return (
        <div className="fund-trends-container" ref={panoramaCaptureRef}>
            {pageHeader}
            <Spin spinning={loadingUsers}>
                <Collapse
                    destroyOnHidden
                    activeKey={adminPanoramaKey}
                    onChange={(keys) => setAdminPanoramaKey(Array.isArray(keys) ? keys : [keys])}
                    className="admin-user-panorama-collapse"
                    style={{ background: "transparent", border: "none" }}
                    items={[
                        {
                            key: "all",
                            label: "全平台汇总（全部用户数据）",
                            children: <FundTrendsChartBody ownerUserId={null} {...bodyCommon} />,
                        },
                        ...adminUsers
                            .filter((u) => u.role === "user")
                            .map((u) => ({
                                key: String(u.id),
                                label: `${u.username}（ID ${u.id}）${u.email ? ` · ${u.email}` : ""}`,
                                children: <FundTrendsChartBody ownerUserId={u.id} {...bodyCommon} />,
                            })),
                    ]}
                />
            </Spin>
        </div>
    );
}

export default FundTrendsChart;
